export const M8_UNIT_4 = {
  id: "m8-u4-perfect-forms",
  title: "Unit 4: Results & Ongoing Activities",
  grammarText: "Present Perfect Simple (I've fixed it - completed/result) vs Present Perfect Continuous (I've been fixing it - ongoing/evidence). State verbs (love, know, understand) vs Action verbs (work, fix, study). Choosing the right form based on context.",
  grammarImage: "/grammar/m8-u4.jpg",
  sets: [

    // SET 1: Completed Results (PP Simple)
    {
      id: "m8-u4-s1",
      title: "Set 1: Finished Tasks",
      context: `*Showing completed work*\n\n"Look! I've fixed it! Now it works perfectly!"\n\n*Proud moment*\n\nTask done. Results visible.`,
      questions: [
        {type: "passage", passage: `"Finally! I've fixed the car! It was broken all week but now I can drive to work again."\n"That's great! How does it feel?"\n"Amazing! I'm so relieved!"`},
        {type: "mc", q: "Car status now?", options: ["Fixed/working", "Still broken", "Being repaired"], a: "Fixed/working", tip: "Completed = PP simple shows result."},
        {type: "order", q: "Traduce: 'He arreglado el carro'", words: ["I've", "fixed", "the", "car."], a: "I've fixed the car.", extra: ["I've been fixing", "I fix"], tip: "Fixed = completed action, ready to use."},
        {type: "mc", q: "Can he drive now?", options: ["Yes", "No", "Maybe"], a: "Yes"},
        {type: "input", q: "I've ___ the car.", a: "fixed"},
        {type: "passage", passage: `"I've cooked dinner! It's ready. Come and eat!"\n"Smells delicious! What did you make?"\n"Pasta with vegetables. I've set the table too."`},
        {type: "mc", q: "Dinner status?", options: ["Ready to eat", "Still cooking", "Not started"], a: "Ready to eat", tip: "Ready = completed, use PP simple."},
        {type: "order", q: "Traduce: 'He cocinado la cena'", words: ["I've", "cooked", "dinner."], a: "I've cooked dinner.", extra: ["I've been cooking", "I cook"], tip: "Cooked = finished, result is ready meal."},
        {type: "input", q: "I've ___ the table.", a: "set"},
        {type: "order", q: "Complete result: I've finished my homework! Now I ___ relax!", words: ["can"], a: "I've finished my homework! Now I can relax!", successTip: "PP SIMPLE RESULT! 'I've done it!' = completed. Focus on finished state, not process. ✅"}
      ]
    },

    // SET 2: Ongoing Process (PPC)
    {
      id: "m8-u4-s2",
      title: "Set 2: Still In Progress",
      context: `*Middle of activity*\n\n"I've been fixing it for hours. Still not done!"\n\n*Work continues*\n\nMess everywhere. Ongoing effort.`,
      questions: [
        {type: "passage", passage: `"You're covered in oil! What have you been doing?"\n"I've been fixing my car all afternoon. It's so complicated! I still haven't finished."`},
        {type: "mc", q: "Car status?", options: ["Still being fixed", "Fixed/ready", "Not started"], a: "Still being fixed", tip: "Ongoing = PPC shows continuing action."},
        {type: "order", q: "Traduce: 'He estado arreglando mi carro'", words: ["I've", "been", "fixing", "my", "car."], a: "I've been fixing my car.", extra: ["I've fixed", "I fix"], tip: "Been fixing = ongoing, not finished yet."},
        {type: "mc", q: "Is it finished?", options: ["No, still working", "Yes, ready", "Almost done"], a: "No, still working"},
        {type: "input", q: "I've been ___ it all afternoon.", a: "fixing"},
        {type: "passage", passage: `"The kitchen is a mess!"\n"I know! I've been cooking for an hour. The food will be ready soon though. I'm almost done."`},
        {type: "mc", q: "Food status?", options: ["Still cooking", "Ready now", "Not started"], a: "Still cooking", tip: "Process ongoing = PPC."},
        {type: "order", q: "Traduce: 'He estado cocinando por una hora'", words: ["I've", "been", "cooking", "for", "an", "hour."], a: "I've been cooking for an hour.", extra: ["I've cooked"], tip: "Been cooking = emphasizes duration, still going."},
        {type: "input", q: "I've ___ cooking for an hour.", a: "been"},
        {type: "order", q: "Ongoing: I've been working all day. I ___ finished yet!", words: ["haven't"], a: "I've been working all day. I haven't finished yet!", successTip: "PPC ONGOING! 'I've been doing it' = process continues. Shows effort/duration, not completion. ⏳"}
      ]
    },

    // SET 3: Evidence Contrast
    {
      id: "m8-u4-s3",
      title: "Set 3: Evidence-Based Choice",
      context: `*Noticing results vs process*\n\nChoosing form based on what you see.\n\n*Detective observation*\n\nClues everywhere.`,
      questions: [
        {type: "passage", passage: `Situation 1: "The room looks beautiful now!"\n"Thanks! I've painted it. Took me all weekend but it's finished!"\n\nSituation 2: "You're covered in paint!"\n"I know! I've been painting the bedroom. Still have two walls left."`},
        {type: "mc", q: "Situation 1: Room is ___?", options: ["Finished/beautiful", "Still being painted", "Messy"], a: "Finished/beautiful", tip: "Beautiful now = completed result."},
        {type: "order", q: "Traduce: 'Lo he pintado'", words: ["I've", "painted", "it."], a: "I've painted it.", extra: ["I've been painting"], tip: "Painted = done, shows completion."},
        {type: "mc", q: "Situation 2: Still painting?", options: ["Yes, 2 walls left", "No, finished", "Starting tomorrow"], a: "Yes, 2 walls left"},
        {type: "input", q: "Covered in paint = I've ___ painting.", a: "been"},
        {type: "passage", passage: `"You look exhausted!"\n"I am! I've been running for 30 minutes. I'm training for a marathon."\n\nVersus:\n\n"Great news! I've run a marathon! I finished it this morning!"`},
        {type: "mc", q: "First person: Still running?", options: ["Yes, training ongoing", "No, finished", "Just started"], a: "Yes, training ongoing", tip: "Exhausted + duration = PPC."},
        {type: "order", q: "Traduce: 'He estado corriendo'", words: ["I've", "been", "running."], a: "I've been running.", extra: ["I've run"], tip: "Been running = explains why tired."},
        {type: "input", q: "I've ___ a marathon! (completed)", a: "run"},
        {type: "order", q: "Evidence choice: Hands dirty? I've been ___. Clean result? I've ___ it!", words: ["gardening", "finished"], a: "Hands dirty? I've been gardening. Clean result? I've finished it!", successTip: "EVIDENCE PRO! Evidence of process → PPC. Evidence of result → PP simple. Choose by what you see! 👁️"}
      ]
    },

    // SET 4: Duration vs Completion
    {
      id: "m8-u4-s4",
      title: "Set 4: Time Focus",
      context: `*Emphasizing different aspects*\n\nDuration of effort vs final result.\n\n*Two perspectives*\n\nSame activity, different focus.`,
      questions: [
        {type: "passage", passage: `"How's the book?"\n"I've been reading it for weeks. It's so long but really interesting!"\n\nVersus:\n\n"I've read the whole book! It was amazing. You can borrow it now."`},
        {type: "mc", q: "First person: Book status?", options: ["Still reading", "Finished", "Not started"], a: "Still reading", tip: "For weeks + still interesting = ongoing."},
        {type: "order", q: "Traduce: 'He estado leyéndolo'", words: ["I've", "been", "reading", "it."], a: "I've been reading it.", extra: ["I've read"], tip: "Been reading = emphasizes ongoing process."},
        {type: "mc", q: "Second person: Can lend it?", options: ["Yes, finished", "No, still reading", "Maybe"], a: "Yes, finished"},
        {type: "input", q: "I've ___ the whole book. (completed)", a: "read"},
        {type: "passage", passage: `"You've been eating all the cookies! There's only one left!"\n\nVersus:\n\n"Oh no! I've eaten all the cookies! The plate is empty. Sorry!"`},
        {type: "mc", q: "First: How many left?", options: ["One", "None", "Many"], a: "One", tip: "Been eating = ongoing, some remain."},
        {type: "order", q: "Traduce: 'Has estado comiendo'", words: ["You've", "been", "eating."], a: "You've been eating.", extra: ["You've eaten"], tip: "Been eating = process observed."},
        {type: "input", q: "I've ___ all the cookies! (none left)", a: "eaten"},
        {type: "order", q: "Choose: Still going? I've been ___. All gone? I've ___ it all!", words: ["working", "finished"], a: "Still going? I've been working. All gone? I've finished it all!", successTip: "DURATION vs RESULT! PPC = emphasize time/effort. PP = emphasize completion/outcome. Context decides! 🎯"}
      ]
    },

    // SET 5: State Verbs Introduction
    {
      id: "m8-u4-s5",
      title: "Set 5: Feelings & Knowledge",
      context: `*Talking about emotions and understanding*\n\nSome verbs describe states, not actions.\n\n*Inner world*\n\nFeelings and thoughts.`,
      questions: [
        {type: "passage", passage: `"I've always loved classical music. Since I was a child, it makes me happy."\n"Really? I've never understood it. I prefer rock music!"`},
        {type: "mc", q: "Loves music since ___?", options: ["Childhood", "Recently", "Never"], a: "Childhood"},
        {type: "order", q: "Traduce: 'Siempre he amado'", words: ["I've", "always", "loved."], a: "I've always loved.", extra: ["I've been loving", "I'm loving"], tip: "Love = state verb, no -ing form. 'I've loved' not 'been loving.'"},
        {type: "mc", q: "Which is correct?", options: ["I've understood", "I've been understanding"], a: "I've understood", tip: "Understand = state verb (feeling/knowledge), NO continuous."},
        {type: "input", q: "I've never ___ classical music.", a: "understood"},
        {type: "passage", passage: `"I've known Sarah for 10 years. She's my best friend."\n"That's wonderful! I've wanted to meet her for ages!"`},
        {type: "mc", q: "How long knows Sarah?", options: ["10 years", "Ages", "Recently"], a: "10 years"},
        {type: "order", q: "Traduce: 'He conocido a Sarah'", words: ["I've", "known", "Sarah."], a: "I've known Sarah.", extra: ["I've been knowing"], tip: "Know = state verb. 'I've known' not 'been knowing.'"},
        {type: "input", q: "I've ___ to meet her.", a: "wanted"},
        {type: "order", q: "State verbs: I've always ___ you. (love, not been loving)", words: ["loved"], a: "I've always loved you.", successTip: "STATE VERBS! Love, know, understand, want, believe = NO -ing. They describe states, not actions. 'I've known' ✓ 'I've been knowing' ✗ 💭"}
      ]
    },

    // SET 6: State Verbs Practice
    {
      id: "m8-u4-s6",
      title: "Set 6: More State Verbs",
      context: `*Expressing beliefs and needs*\n\nStates of mind can't be continuous.\n\n*Mental states*\n\nWhat you think and feel.`,
      questions: [
        {type: "passage", passage: `"I've believed in this project from the start. It's going to be successful!"\n"I agree! I've needed this opportunity for so long. It's finally happening!"`},
        {type: "mc", q: "Which is correct for belief?", options: ["I've believed", "I've been believing"], a: "I've believed", tip: "Believe = state verb, describes mental state, NO continuous."},
        {type: "order", q: "Traduce: 'He creído en esto'", words: ["I've", "believed", "in", "this."], a: "I've believed in this.", extra: ["I've been believing"], tip: "Believed = state of mind, not action."},
        {type: "mc", q: "Which is correct for need?", options: ["I've needed", "I've been needing"], a: "I've needed", tip: "Need = state verb, NO -ing."},
        {type: "input", q: "I've ___ this for so long.", a: "needed"},
        {type: "passage", passage: `"Do you have a car?"\n"Yes, I've had it for three years. It's very reliable."\n"Lucky you! I've wanted a car forever but can't afford one."`},
        {type: "mc", q: "Has car how long?", options: ["3 years", "Forever", "Recently"], a: "3 years"},
        {type: "order", q: "Traduce: 'Lo he tenido'", words: ["I've", "had", "it."], a: "I've had it.", extra: ["I've been having"], tip: "Have (possession) = state verb. 'I've had' not 'been having.'"},
        {type: "input", q: "I've ___ a car forever.", a: "wanted"},
        {type: "order", q: "State ownership: I've ___ this house for 5 years. (had, not been having)", words: ["had"], a: "I've had this house for 5 years.", successTip: "STATE POSSESSION! Have (own), need, want, believe = mental/ownership states. Always simple form. 'I've had' ✓ 🏠"}
      ]
    },

    // SET 7: State vs Action Contrast
    {
      id: "m8-u4-s7",
      title: "Set 7: States vs Actions",
      context: `*Mixing both types*\n\nActions can be continuous, states cannot.\n\n*Real conversations*\n\nBoth verb types together.`,
      questions: [
        {type: "passage", passage: `"I've loved this city since I moved here. I've been living here for 5 years now and I've been exploring every neighborhood!"`},
        {type: "mc", q: "Loved (state) or been loving?", options: ["Loved", "Been loving"], a: "Loved", tip: "Love = state, can't be continuous."},
        {type: "order", q: "Traduce: 'He estado viviendo aquí'", words: ["I've", "been", "living", "here."], a: "I've been living here.", extra: ["I've lived"], tip: "Living = action, CAN be continuous."},
        {type: "mc", q: "Exploring (action): which form?", options: ["Been exploring", "Explored"], a: "Been exploring", tip: "Explore = action, ongoing process."},
        {type: "input", q: "I've ___ this city. (state: love)", a: "loved"},
        {type: "passage", passage: `"I've known Tom for years, but I've been working with him for only six months. I've always respected his talent."`},
        {type: "mc", q: "Known (state) or been knowing?", options: ["Known", "Been knowing"], a: "Known", tip: "Know = state verb, NO continuous."},
        {type: "order", q: "Traduce: 'He estado trabajando con él'", words: ["I've", "been", "working", "with", "him."], a: "I've been working with him.", extra: ["I've worked"], tip: "Work = action, can show duration with PPC."},
        {type: "input", q: "I've ___ his talent. (state: respect)", a: "respected"},
        {type: "order", q: "Mix: I've ___ (know) her for years and ___ been working together!", words: ["known", "we've"], a: "I've known her for years and we've been working together!", successTip: "STATE vs ACTION! States (love, know) = simple. Actions (work, study) = can be continuous. Learn the difference! 🧠⚡"}
      ]
    },

    // SET 8: Common Mistakes
    {
      id: "m8-u4-s8",
      title: "Set 8: Avoiding Errors",
      context: `*Typical learner mistakes*\n\nPracticing correct forms.\n\n*Error correction*\n\nLearning from mistakes.`,
      questions: [
        {type: "passage", passage: `WRONG: "I've been loving pizza all my life."\nCORRECT: "I've loved pizza all my life."\n\nWRONG: "She's been knowing the answer."\nCORRECT: "She's known the answer."`},
        {type: "mc", q: "Which is correct?", options: ["I've loved", "I've been loving"], a: "I've loved", tip: "Love = state, NEVER continuous."},
        {type: "order", q: "Traduce correcto: 'Siempre he querido esto'", words: ["I've", "always", "wanted", "this."], a: "I've always wanted this.", extra: ["been wanting"], tip: "Want = state, use simple form."},
        {type: "mc", q: "Fix: She's been knowing → She's ___", options: ["known", "been knowing"], a: "known", tip: "Know = state verb."},
        {type: "input", q: "I've ___ English. (understand, not been understanding)", a: "understood"},
        {type: "passage", passage: `WRONG: "I've had dinner." (when still eating)\nCORRECT: "I've been having dinner." (action in progress)\n\nBut for possession:\nCORRECT: "I've had this phone for two years." (state of ownership)`},
        {type: "mc", q: "Have as action (eating): which form?", options: ["Been having", "Had"], a: "Been having", tip: "Have (eat) = action, can be continuous."},
        {type: "order", q: "Traduce: 'He tenido este teléfono'", words: ["I've", "had", "this", "phone."], a: "I've had this phone.", extra: ["been having"], tip: "Have (own) = state, simple form."},
        {type: "input", q: "Possession: I've ___ it for years.", a: "had"},
        {type: "order", q: "Correct: I've ___ (know) the truth all along!", words: ["known"], a: "I've known the truth all along!", successTip: "ERROR AVOIDED! State verbs NEVER continuous. Action verbs CAN be. 'I've known' ✓ 'I've been knowing' ✗ Perfect! ✅"}
      ]
    },

    // SET 9: Real Context Choices
    {
      id: "m8-u4-s9",
      title: "Set 9: Choosing Correctly",
      context: `*Real-life decisions*\n\nPicking the right form based on meaning.\n\n*Context is king*\n\nMeaning determines form.`,
      questions: [
        {type: "passage", passage: `"How's your project going?"\n"Great! I've been working on it for months. Almost finished now."\n"That's dedication! I've always admired your commitment."`},
        {type: "mc", q: "Project: ongoing or finished?", options: ["Ongoing, almost done", "Completely finished", "Not started"], a: "Ongoing, almost done", tip: "Been working = ongoing process."},
        {type: "order", q: "Traduce: 'He estado trabajando en ello'", words: ["I've", "been", "working", "on", "it."], a: "I've been working on it.", extra: ["I've worked"], tip: "PPC shows duration and ongoing effort."},
        {type: "mc", q: "Admired: state or action?", options: ["State (always admired)", "Action (been admiring)"], a: "State (always admired)", tip: "Admire feelings = state verb."},
        {type: "input", q: "I've always ___ your work. (admire)", a: "admired"},
        {type: "passage", passage: `"The car is ready! I've fixed it! You can drive now."\n"Thank you! How long did it take?"\n"Well, I've been working on it all morning, but it's done now!"`},
        {type: "mc", q: "Car status now?", options: ["Fixed/ready", "Still broken", "Being fixed"], a: "Fixed/ready", tip: "Fixed = completed, PP simple."},
        {type: "order", q: "Traduce resultado: 'Lo he arreglado'", words: ["I've", "fixed", "it."], a: "I've fixed it.", extra: ["been fixing"], tip: "Fixed = emphasizes completion."},
        {type: "input", q: "Duration: I've ___ working on it. (been)", a: "been"},
        {type: "order", q: "Complete story: I've been repairing it for hours. Now I've ___ it!", words: ["finished"], a: "I've been repairing it for hours. Now I've finished it!", successTip: "CONTEXT CHOICE! Ongoing process? PPC. Completed result? PP. Feelings/states? Always simple. Master the logic! 🎯"}
      ]
    },

    // SET 10: B2 Integration Mastery
    {
      id: "m8-u4-s10",
      title: "Set 10: Perfect Forms Mastery",
      context: `*EducaT Progress Reports*\n\nStudents sharing achievements and ongoing work.\n\n*Final presentations*\n\nMixing all forms correctly.`,
      questions: [
        {type: "passage", passage: `"Welcome! Share your progress!"\n"I've been studying English for two years now. I've learned so much! I've always loved languages, so this has been perfect for me. I've finished three courses and I've been preparing for my B2 exam!"`},
        {type: "mc", q: "Studying: how long?", options: ["2 years", "3 courses", "B2 exam"], a: "2 years", tip: "Been studying = ongoing duration."},
        {type: "order", q: "Traduce estado: 'Siempre he amado'", words: ["I've", "always", "loved."], a: "I've always loved.", extra: ["been loving"], tip: "Love = state, simple form only."},
        {type: "mc", q: "Courses: finished or ongoing?", options: ["Finished (completed)", "Still taking", "Not started"], a: "Finished (completed)", tip: "Finished = PP simple, shows completion."},
        {type: "input", q: "I've been ___ for my exam.", a: "preparing"},
        {type: "passage", passage: `"My turn! I've been working on a big project. I've wanted to do this for years! I haven't finished yet, but I've already completed the first three chapters. I've been writing every day for six months."`},
        {type: "mc", q: "Project status?", options: ["Not finished yet", "Completely done", "Not started"], a: "Not finished yet", tip: "Been working = ongoing."},
        {type: "order", q: "Traduce: 'He querido hacer esto'", words: ["I've", "wanted", "to", "do", "this."], a: "I've wanted to do this.", extra: ["been wanting"], tip: "Want = state verb, NO continuous."},
        {type: "input", q: "I've ___ the first chapters. (complete)", a: "completed"},
        {type: "passage", passage: `"Amazing work everyone! I can see you've all been working very hard. I've known some of you for years, and I've always believed in your potential. You've improved so much!"`},
        {type: "mc", q: "Known (state) or been knowing?", options: ["Known", "Been knowing"], a: "Known", tip: "Know = state, simple form."},
        {type: "order", q: "Traduce: 'Todos han estado trabajando'", words: ["You've", "all", "been", "working."], a: "You've all been working.", extra: ["worked"], tip: "Been working = emphasizes effort."},
        {type: "order", q: "Ultimate mix: I've ___ (know) you for years, seen you ___ been working hard, and you've ___ (finish) brilliantly!", words: ["known", "have", "finished"], a: "I've known you for years, seen you have been working hard, and you've finished brilliantly!", successTip: "M8-U4 CONQUERED! PP simple (results/states) vs PPC (ongoing/evidence). State verbs NEVER continuous. Action verbs choose by context. B2 precision mastered! 🏆🎯✨"}
      ]
    }
  ],

  readingSet: {
    id: "m8-u4-reading",
    title: "Reading: Perfect Forms in Context",
    category: "reading",
    questions: [
      {
        type: "passage",
        passage: `**Property Expert Blog**\n\nI've always loved property. It's fascinating to see how people decorate their homes. I've been viewing apartments with friends for years because I can easily imagine how places might look. In my current apartment, I've been repairing hundreds of little faults. My friend Sadiq has completely rebuilt his apartment in the same time!`
      },
      {
        type: "mc",
        q: "Loved property: always or recently?",
        options: ["Always (state)", "Recently started", "Never"],
        a: "Always (state)",
        tip: "Love = state verb, I've loved ✓"
      },
      {
        type: "order",
        q: "Traduce: 'He estado viendo apartamentos'",
        words: ["I've", "been", "viewing", "apartments."],
        a: "I've been viewing apartments.",
        tip: "Viewing = action, ongoing process."
      },
      {
        type: "mc",
        q: "Sadiq's apartment: finished or ongoing?",
        options: ["Finished (rebuilt)", "Still working", "Not started"],
        a: "Finished (rebuilt)",
        tip: "Rebuilt = completed, PP simple."
      },
      {
        type: "passage",
        passage: `**Car Repair Story**\n\n"Finally finished! I've been fixing my car all weekend. I'm exhausted and covered in oil, but I've fixed it! The engine was broken but now it works perfectly. I've always enjoyed working with cars, even though it's hard work."`
      },
      {
        type: "mc",
        q: "Car now?",
        options: ["Fixed/working", "Still broken", "Being repaired"],
        a: "Fixed/working"
      },
      {
        type: "input",
        q: "I've ___ fixing it. (been = ongoing)",
        a: "been"
      },
      {
        type: "mc",
        q: "Enjoyed cars: state or action?",
        options: ["State (always enjoyed)", "Action (been enjoying)"],
        a: "State (always enjoyed)",
        tip: "Enjoy feelings = state here."
      },
      {
        type: "passage",
        passage: `**Cooking Diary**\n\nSunday: I've been cooking for three hours! Kitchen is a mess but the food smells amazing. Almost ready now.\n\nMonday: Last night's dinner was perfect! I've cooked Italian food many times, but this was my best. Everyone loved it!`
      },
      {
        type: "order",
        q: "Traduce domingo: 'He estado cocinando'",
        words: ["I've", "been", "cooking."],
        a: "I've been cooking.",
        successTip: "READING COMPLETE! Choosing PP vs PPC by context mastered."
      },
      {
        type: "mc",
        q: "Monday: dinner was ___?",
        options: ["Perfect/finished", "Still cooking", "Not good"],
        a: "Perfect/finished"
      }
    ]
  },

  reviewSet: {
    id: "m8-u4-review",
    title: "Unit 4 Mastery Review",
    category: "review",
    questions: [
      {
        type: "passage",
        passage: `"You're exhausted!"\n"I've been studying all night. I still haven't finished the chapter but I've learned a lot. I've always wanted to understand this topic."`
      },
      {
        type: "mc",
        q: "Studying: finished or ongoing?",
        options: ["Ongoing (not finished)", "Finished", "Not started"],
        a: "Ongoing (not finished)",
        tip: "Been studying = ongoing process."
      },
      {
        type: "order",
        q: "Traduce estado: 'Siempre he querido'",
        words: ["I've", "always", "wanted."],
        a: "I've always wanted.",
        tip: "Want = state, NO continuous."
      },
      {
        type: "mc",
        q: "What's completed?",
        options: ["Learned a lot", "Finished chapter", "Started studying"],
        a: "Learned a lot"
      },
      {
        type: "passage",
        passage: `"The project is ready! I've finished it!"\n"Finally! How long were you working on it?"\n"I've been working on it for months, but it's done now!"`
      },
      {
        type: "input",
        q: "I've ___ it! (finished = completed)",
        a: "finished"
      },
      {
        type: "mc",
        q: "Been working: why this form?",
        options: ["Shows duration/effort", "Shows completion", "Shows state"],
        a: "Shows duration/effort",
        tip: "PPC emphasizes process time."
      },
      {
        type: "order",
        q: "Result vs process: I've ___ working for hours. Now I've ___ it!",
        words: ["been", "finished"],
        a: "I've been working for hours. Now I've finished it!"
      },
      {
        type: "passage",
        passage: `"I've known Maria for 10 years. She's amazing! I've always respected her work. Recently she's been teaching me guitar."`
      },
      {
        type: "mc",
        q: "Known: why not 'been knowing'?",
        options: ["Know = state verb", "Know = action verb", "Random choice"],
        a: "Know = state verb",
        tip: "State verbs NEVER continuous."
      },
      {
        type: "input",
        q: "I've ___ respected her. (always)",
        a: "always"
      },
      {
        type: "order",
        q: "Ultimate mastery: I've ___ (know) you forever, ___ been watching you grow, and you've ___ (become) amazing!",
        words: ["known", "I've", "become"],
        a: "I've known you forever, I've been watching you grow, and you've become amazing!",
        successTip: "M8-U4 MASTERED! PP simple (results/completion/states) vs PPC (ongoing/duration/evidence). State verbs = simple only. Perfect B2 precision! 🌟🏆"
      }
    ]
  }
};
